import React from 'react';
import { Link } from 'react-router-dom';
import {
  FiArrowLeft,
  FiExternalLink,
  FiActivity,
  FiCpu,
  FiCreditCard,
  FiShare2,
  FiUsers,
  FiFileText,
  FiLock,
  FiArrowRight,
} from 'react-icons/fi';
import SEO from '../../components/SEO';
import './CaseStudy.css';

const ACCENT = '#3B82F6';
const LIVE_URL = 'https://mymedicalcabinet.com/';
const IMG_MAC =
  'https://silverfoxmedia.s3.amazonaws.com/MMCscreenhp-front.png';
const IMG_PHONE =
  'https://silverfoxmedia.s3.amazonaws.com/Screenshot%202026-07-13%20at%2010.51.41%E2%80%AFAM-portrait.png';

const MyMedicalCabinetCaseStudy = () => {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: 'MyMedicalCabinet — Case Study',
    headline: 'Patient-Owned Medical Records + AI Bill Analysis',
    description:
      "How SilverFox Media designed and shipped MyMedicalCabinet: a HIPAA-ready patient records PWA that pulls from 493+ Epic FHIR endpoints, runs Anthropic Claude over medical bills to catch CPT-code errors, and includes a Stripe Connect marketplace for disputing overcharges.",
    image: IMG_MAC,
    url: 'https://silverfoxmedia.co/portfolio/mymedicalcabinet',
    author: {
      '@type': 'Organization',
      name: 'SilverFox Media',
      url: 'https://silverfoxmedia.co',
    },
    about: [
      { '@type': 'Thing', name: 'Epic FHIR Integration' },
      { '@type': 'Thing', name: 'SMART on FHIR' },
      { '@type': 'Thing', name: 'HIPAA' },
      { '@type': 'Thing', name: 'Stripe Connect' },
      { '@type': 'Thing', name: 'Anthropic Claude' },
    ],
  };

  return (
    <>
      <SEO
        title="MyMedicalCabinet Case Study: Patient Records PWA + AI Bill Analysis"
        description="How SilverFox Media designed and shipped MyMedicalCabinet: a HIPAA-ready patient records PWA that pulls from 493+ Epic FHIR endpoints, runs Anthropic Claude over medical bills to catch CPT-code errors, and includes a Stripe Connect marketplace for disputing overcharges."
        keywords="MyMedicalCabinet case study, Epic FHIR integration, SMART on FHIR, HIPAA PWA, Anthropic Claude medical bill analysis, CPT code parsing, No Surprises Act, Stripe Connect marketplace, patient records app"
        image="https://silverfoxmedia.co/images/og/mymedicalcabinet.jpg"
        url="/portfolio/mymedicalcabinet"
        type="article"
        schema={structuredData}
      />

      <article
        className="case-study"
        style={{ '--case-study-accent': ACCENT }}
      >
        {/* HERO — 2-col on desktop, media hidden on mobile */}
        <section className="case-study__hero">
          <div className="container">
            <div className="case-study__hero-grid">
              <div className="case-study__hero-inner">
                <Link to="/portfolio" className="case-study__back">
                  <FiArrowLeft />
                  Back to Portfolio
                </Link>

                <span className="case-study__category">Healthcare</span>

                <h1 className="case-study__title">
                  MyMedicalCabinet:{' '}
                  <span className="case-study__title-accent">
                    Patient-Owned Records + AI Bill Review
                  </span>
                </h1>

                <p className="case-study__tagline">
                  A HIPAA-ready Progressive Web App that pulls a patient's records from
                  493+ Epic health systems, runs Anthropic Claude over their medical
                  bills to catch CPT-code errors and No Surprises Act violations, and
                  routes disputes through a Stripe Connect marketplace to the biller.
                </p>

                <div className="case-study__hero-cta">
                  <a
                    href={LIVE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="case-study__cta-primary"
                  >
                    Visit Live Site <FiExternalLink />
                  </a>
                  <Link to="/contact" className="case-study__cta-secondary">
                    Start Your Project <FiArrowRight />
                  </Link>
                </div>

                <div className="case-study__hero-meta">
                  <div className="case-study__hero-meta-item">
                    <span className="case-study__hero-meta-label">Client</span>
                    <span className="case-study__hero-meta-value">MyMedicalCabinet</span>
                  </div>
                  <div className="case-study__hero-meta-item">
                    <span className="case-study__hero-meta-label">Industry</span>
                    <span className="case-study__hero-meta-value">Healthcare / Consumer</span>
                  </div>
                  <div className="case-study__hero-meta-item">
                    <span className="case-study__hero-meta-label">Timeline</span>
                    <span className="case-study__hero-meta-value">2026 — Ongoing</span>
                  </div>
                  <div className="case-study__hero-meta-item">
                    <span className="case-study__hero-meta-label">Role</span>
                    <span className="case-study__hero-meta-value">Sole Developer</span>
                  </div>
                </div>
              </div>

              <div className="case-study__hero-media">
                <img
                  src={IMG_MAC}
                  alt="MyMedicalCabinet dashboard on a MacBook mockup"
                  className="case-study__hero-macbook"
                  loading="lazy"
                />
                <img
                  src={IMG_PHONE}
                  alt="MyMedicalCabinet on a portrait mobile screen"
                  className="case-study__hero-phone"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        {/* FEATURED IMAGE — mobile only, hidden on desktop via CSS */}
        <section className="case-study__featured">
          <div className="container">
            <div className="case-study__featured-image-wrap">
              <img
                src={IMG_MAC}
                alt="MyMedicalCabinet dashboard on a MacBook mockup"
                className="case-study__featured-image"
                loading="lazy"
              />
              <img
                src={IMG_PHONE}
                alt="MyMedicalCabinet on a portrait mobile screen"
                className="case-study__featured-phone"
                loading="lazy"
              />
            </div>
          </div>
        </section>

        {/* OVERVIEW */}
        <section className="case-study__section">
          <div className="container">
            <p className="case-study__section-label">The Overview</p>
            <h2 className="case-study__section-title">
              A patient's records + a way to fight the bill, in one app.
            </h2>
            <div className="case-study__prose">
              <p>
                MyMedicalCabinet started with a simple observation: the average
                American patient's medical records live in a dozen different EHR
                portals with a dozen different passwords, and their medical bills
                arrive with billing codes almost no one can decipher. The product
                consolidates both — one PWA that imports FHIR-standard records from
                493+ live Epic health systems and runs an Anthropic Claude analysis
                over uploaded bills to spot CPT-code errors, CMS Medicare fair-price
                gaps, and No Surprises Act violations.
              </p>
              <p>
                When Claude flags a real error, the app hands the patient a Stripe
                Connect marketplace flow to route the dispute directly to the biller
                — the biller onboards through Stripe Express, the counter-offer
                negotiation is in-app, and MyMedicalCabinet takes a 10% platform fee
                only when a settlement lands. Records, review, and remediation live under
                one login.
              </p>
            </div>
          </div>
        </section>

        {/* CHALLENGE */}
        <section className="case-study__section" style={{ background: 'var(--color-gray-100)' }}>
          <div className="container">
            <p className="case-study__section-label">The Challenge</p>
            <h2 className="case-study__section-title">
              HIPAA, 493 Epic tenants, and a dispute flow with real money.
            </h2>
            <div className="case-study__prose">
              <p>
                Three problems shaped the architecture. First, HIPAA sets a high bar
                for any product touching patient records — authentication, transport
                encryption at rest, granular access logging, and a defensible
                consent-versioning story. Every design decision had to hold up under
                that lens from day one, not bolted on later.
              </p>
              <p>
                Second, Epic's FHIR ecosystem is 493+ live health-system endpoints,
                each with its own OAuth 2.0 configuration and its own quirks. Storing
                per-tenant credentials in the clear wasn't an option — every
                credential is encrypted at rest with AES-256-GCM and keyed to the
                individual health system so a leaked key can be rotated without
                re-onboarding every patient on that system.
              </p>
              <p>
                Third, the bill-dispute flow moves real money between three parties
                (patient, biller, platform) and had to feel like Stripe, not like a
                healthcare portal. Marketplaces with counter-offer negotiation are
                already tricky — adding a compliance layer on top made every state
                transition load-bearing.
              </p>
            </div>
          </div>
        </section>

        {/* SOLUTION / KEY FEATURES */}
        <section className="case-study__section">
          <div className="container">
            <p className="case-study__section-label">The Solution</p>
            <h2 className="case-study__section-title">
              MERN stack, Claude on the analysis path, Stripe Connect on the payout path.
            </h2>
            <p className="case-study__section-lead">
              MyMedicalCabinet is a Progressive Web App on the classic MERN stack
              (React 18, Node, Mongoose, MongoDB) with 20+ API route files, 14
              Mongoose models, and 53+ React components. Claude and Stripe Connect
              are the two non-standard dependencies — everything else is plain,
              inspectable JavaScript.
            </p>

            <div className="case-study__features">
              <div className="case-study__feature">
                <span className="case-study__feature-icon"><FiActivity /></span>
                <h3 className="case-study__feature-title">Epic FHIR Integration at Scale</h3>
                <p className="case-study__feature-body">
                  OAuth 2.0 flows against 493 live Epic endpoints with per-health-
                  system AES-256-GCM encrypted credentials. Imports 14+ clinical
                  resource types — medications, conditions, labs, immunizations,
                  encounters — into a normalized Mongo projection.
                </p>
              </div>

              <div className="case-study__feature">
                <span className="case-study__feature-icon"><FiCpu /></span>
                <h3 className="case-study__feature-title">AI Medical Bill Review</h3>
                <p className="case-study__feature-body">
                  Anthropic Claude parses uploaded bills for CPT codes, matches
                  against CMS Medicare fair-price data, and flags No Surprises Act
                  violations. Analysis results attach to the document
                  subdocument for downstream dispute flows.
                </p>
              </div>

              <div className="case-study__feature">
                <span className="case-study__feature-icon"><FiCreditCard /></span>
                <h3 className="case-study__feature-title">Stripe Connect Marketplace</h3>
                <p className="case-study__feature-body">
                  Billers onboard through Stripe Express. Patients open a dispute,
                  the biller responds with a counter-offer, both sides negotiate
                  in-app, and settlements route via Stripe with a 10% platform fee.
                  SendGrid delivers state-change notifications end-to-end.
                </p>
              </div>

              <div className="case-study__feature">
                <span className="case-study__feature-icon"><FiShare2 /></span>
                <h3 className="case-study__feature-title">Multi-Channel Record Sharing</h3>
                <p className="case-study__feature-body">
                  QR code, email-OTP, doctor-account, and temporary-link sharing —
                  each with granular per-category permissions across 7 data
                  categories, bcrypt-hashed OTPs, session tokens, and full access
                  logging.
                </p>
              </div>

              <div className="case-study__feature">
                <span className="case-study__feature-icon"><FiUsers /></span>
                <h3 className="case-study__feature-title">Family Multi-Tenancy</h3>
                <p className="case-study__feature-body">
                  Up to 10 family members per account, each with their own Epic
                  connections and clinical record store. All queries scope by
                  familyMemberId so a mom-of-three managing her own + kids' records
                  never sees cross-tenant leaks.
                </p>
              </div>

              <div className="case-study__feature">
                <span className="case-study__feature-icon"><FiFileText /></span>
                <h3 className="case-study__feature-title">Document Pipeline</h3>
                <p className="case-study__feature-body">
                  Sharp for image compression, Tesseract.js for OCR, PDF text
                  extraction, and S3 presigned uploads for medical documents up to
                  50MB. Every upload runs through the same normalization pipeline
                  before Claude sees it.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* TECH STACK */}
        <section className="case-study__section" style={{ background: 'var(--color-gray-100)' }}>
          <div className="container">
            <p className="case-study__section-label">The Stack</p>
            <h2 className="case-study__section-title">MERN + HIPAA-grade defaults.</h2>
            <p className="case-study__section-lead">
              Every dependency was picked with an audit trail in mind. No
              black-box services in the request path — if something goes wrong,
              the incident response can be reconstructed from Mongo and the
              filesystem alone.
            </p>

            <div className="case-study__tech">
              <span className="case-study__tech-chip">React 18</span>
              <span className="case-study__tech-chip">PWA</span>
              <span className="case-study__tech-chip">Node.js</span>
              <span className="case-study__tech-chip">Express</span>
              <span className="case-study__tech-chip">MongoDB + Mongoose</span>
              <span className="case-study__tech-chip">Epic FHIR R4</span>
              <span className="case-study__tech-chip">SMART on FHIR</span>
              <span className="case-study__tech-chip">OAuth 2.0</span>
              <span className="case-study__tech-chip">AES-256-GCM</span>
              <span className="case-study__tech-chip">JWT</span>
              <span className="case-study__tech-chip">Anthropic Claude</span>
              <span className="case-study__tech-chip">Stripe Connect</span>
              <span className="case-study__tech-chip">Tesseract.js OCR</span>
              <span className="case-study__tech-chip">Sharp</span>
              <span className="case-study__tech-chip">SendGrid</span>
              <span className="case-study__tech-chip">AWS S3</span>
              <span className="case-study__tech-chip">CMS Medicare API</span>
            </div>
          </div>
        </section>

        {/* RESULTS */}
        <section className="case-study__section case-study__section--dark">
          <div className="container">
            <p className="case-study__section-label">The Results</p>
            <h2 className="case-study__section-title">Shipped, HIPAA-ready, and Epic-connected.</h2>
            <p className="case-study__section-lead" style={{ color: 'rgba(255,255,255,0.75)' }}>
              MyMedicalCabinet is live at mymedicalcabinet.com — every request path
              instrumented, every credential encrypted, every record change logged.
            </p>

            <div className="case-study__stats">
              <div className="case-study__stat">
                <p className="case-study__stat-value">493+</p>
                <p className="case-study__stat-label">Live Epic health-system endpoints supported</p>
              </div>
              <div className="case-study__stat">
                <p className="case-study__stat-value">14</p>
                <p className="case-study__stat-label">FHIR clinical resource types imported per patient</p>
              </div>
              <div className="case-study__stat">
                <p className="case-study__stat-value">10 members</p>
                <p className="case-study__stat-label">Family multi-tenancy per account</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="case-study__section">
          <div className="container">
            <div className="case-study__cta">
              <h2 className="case-study__cta-title">Building in a regulated vertical?</h2>
              <p className="case-study__cta-text">
                HIPAA-grade architecture, AI on the sensitive path, and Stripe
                marketplaces with real audit trails — we ship healthcare and
                fintech products like MyMedicalCabinet end-to-end.
              </p>
              <Link to="/contact" className="case-study__cta-btn">
                Start Your Project <FiArrowRight />
              </Link>
            </div>
          </div>
        </section>
      </article>
    </>
  );
};

export default MyMedicalCabinetCaseStudy;
